import React from 'react';
import {
  Container,
  Typography,
  Box,
  Breadcrumbs,
  makeStyles,
  createStyles,
  Theme,
} from '@material-ui/core';
import { Link } from 'gatsby-theme-material-ui';

const useStyles = makeStyles(({ spacing }: Theme) =>
  createStyles({
    root: {
      marginTop: spacing(5),
      marginBottom: spacing(5),
    },
    list: {
      listStyle: 'disc',
      marginTop: 'unset',

      paddingLeft: spacing(3),
      '& li': {
        paddingTop: spacing(1),
      },
      '& ul': {
        listStyle: 'circle',
        '& ul': {
          listStyle: 'square',
        },
      },
    },
  })
);

export const PrivacyPolicy = () => {
  const classes = useStyles();
  return (
    <Container maxWidth="md" className={classes.root}>
      <Breadcrumbs aria-label="breadcrumb">
        <Link color="inherit" to="/">
          Home
        </Link>
        <Typography color="textPrimary">Privacy Policy</Typography>
      </Breadcrumbs>
      <Box mt={2} />
      <Typography variant="h4" component="h1">
        HabFit Privacy Policy
      </Typography>
      <Box mt={3} />
      <Typography variant="subtitle1">Effective: March 30, 2021</Typography>
      <Box mt={1} />
      <Typography>
        Thank you for visiting{' '}
        <Link href="https://habfit.io">habfit.io</Link> ( the “Site”). For
        the purposes of this Privacy Policy, the Site and any interaction with
        us on Facebook Messenger, WhatsApp, SMS, Slack, Viber, and/or any other
        online channels shall be referred to as “Platform”. This Privacy Policy
        describes how HabFit (HabFit Technologies Inc.) collects, uses, and
        shares the information that you provide to us or that we gather from you
        while you use our Platform. The users, who are both clients and
        instructors for the purposes of this Privacy Policy, shall be referred
        to as “you” or “your.”
      </Typography>
      <Box mt={1} />
      <Typography>
        <b>Note</b>: This Privacy Policy only applies to the collection and use
        of data by HabFit. This Privacy Policy does not apply to any product of
        other companies, individuals, or organizations to which we may provide
        links. These sites may have different policies relating to your privacy
        and the information they collect about you. Visitors should consult
        other companies’ privacy policies, as HabFit has no control over
        information that is submitted to, or collected by, third parties.
      </Typography>
      <Box mt={3} />
      <Typography id="personal-data-collection" variant="h5" component="h2">
        Personal Data Collected
      </Typography>
      <Box mt={2} />
      <Typography>
        Personal Data is any and all information that relates to you and
        identifies you as an individual. We collect Personal Data from you in
        two major ways:
      </Typography>
      <ul className={classes.list}>
        <li>
          <Typography>
            information that you provide to us (such as name, billing address,
            email, etc.);
          </Typography>
        </li>
        <li>
          <Typography>
            information about your use of the product (such as technical info
            like cookies, IP address, etc.).
          </Typography>
        </li>
      </ul>
      <Box mt={1} />
      <Typography>
        HabFit only collects the least amount of data required to ensure that
        you have a delightful instructor search and booking experience. We do
        not collect “extra” data that is unnecessary, such as your marital
        status or company name, to provide you with a delightful search for
        instructor and booking experiences.
      </Typography>
      <ul className={classes.list}>
        <li>
          <Typography>
            <b>Personal Information.</b> We collect and use personal information
            about you in the following ways:
          </Typography>
        </li>
        <ul className={classes.list}>
          <li>
            <Typography>
              <i>Registering on the Platform</i>. You may supply HabFit with
              your personal information when you register on the Platform or use
              HabFit’s online channel. For example, a client who registers on
              the Platform must provide a full name, state of residence, email
              address, date of birth, and password. For an instructor to
              register on the Platform, he or she must provide full name, state
              of residence, email address, date of birth, username, password,
              home address, contact number, number of available spots,
              instructor bio, photo, and Social Security Number (For a criminal
              background check), Facebook Messenger or any similar messenger
              platform profile links. HabFit may, from time to time, change the
              information that it requests from the visitors to the Platform.
            </Typography>
          </li>
        </ul>
        <li>
          <Typography>
            <i>Subscription billing</i>. You may supply HabFit with further
            personal information, including your home address and credit card
            information on the Platform. We use a third-party company to process
            billing and credit card transactions, but we may have access to this
            data.
          </Typography>
        </li>
        <li>
          <Typography>
            <i>E-mail</i>. If you submit feedback, questions, or requests for
            information about HabFit services, we may request your name, mailing
            address, telephone number, and e-mail address in order to respond to
            your inquiries. HabFit may collect and store the information you
            choose to provide us to send you information about HabFit from time
            to time.
          </Typography>
        </li>
        <li>
          <Typography>
            <i>Surveys and Promotions</i>. From time to time, we may request
            information from you via surveys or online promotions such as
            contests and sweepstakes. Participation is completely voluntary; you
            have a choice whether or not to disclose any information.
            Information requested may include personal contact information such
            as your name, shipping address, phone number, or e-mail address.
            This information may be used to contact users with further
            information about HabFit products and services, to fulfill the terms
            of the promotion, notify winners and/or award prizes, or to monitor
            or improve the use and satisfaction of this platform.
          </Typography>
        </li>
        <ul className={classes.list}>
          <li>
            <Typography>
              <i>Loyalty Programs and Promotional Services</i>. We may gather
              information from you when you sign up for any loyalty program or
              other promotional offer made via the Platform. As part of those
              services, you may be required to provide personal contact
              information such as your name, shipping address, phone number, or
              e-mail address. This information may be used to contact users with
              further information about HabFit services, to fulfill the terms of
              any training experience, or to monitor or improve the use and
              satisfaction of the Platform.
            </Typography>
          </li>
          <li>
            <Typography>
              <i>Mobile Applications</i>. We also may collect any of the above
              information from users of mobile devices; in such an instance,
              when the collection is occurring by HabFit, the terms of this
              Privacy Policy shall control, unless otherwise noted at the time
              of collection.
            </Typography>
          </li>
          <li>
            <Typography>
              <b>Non-Personally Identifiable Information</b>. We collect and use
              non-personally identifiable information about you in the following
              ways:
            </Typography>
          </li>
          <ul className={classes.list}>
            <li>
              <Typography>
                <i>Cookies</i>. A “cookie” is a small text file that may be used
                to collect information about your activity on the Site. For
                example, when someone visits a page within the Site, a cookie is
                placed on the user’s machine (if the user accepts cookies) or is
                read if the user has visited the Site previously. You may set
                most browsers to notify you if you receive a cookie, or you may
                choose to block cookies with your browser, but if you do, you
                may not be able to take advantage of the personalized features
                enjoyed by other users to our Site. Some of the cookies we use
                may be flash cookies or Adobe cookies. While they are harmless,
                they may contain demographic information, and, depending on your
                browser, these cookies may not normally be deleted when your
                cookies are deleted. Please check your browser to determine
                where these types of cookies are stored and how they may be
                deleted.
              </Typography>
            </li>
            <li>
              <Typography>
                <i>Referrers, IP Addresses, and Environmental Variables</i>.
                HabFit also may collect information through referrers, IP
                addresses, and various environmental variables. A referrer is an
                information the web browser passes along to the HabFit web
                server that references the URL from which you came. An IP
                address is a number used by computers on a network to identify
                your computer so that data can be transmitted to you. An
                environmental variable may include, among other things, the
                domain from which you access the Internet, the time you accessed
                the Site, the type of web browser and operating system or
                platform used, the Internet address or the web site you left to
                visit the Site, the pages you visit while at the Site, and the
                Internet address of the web site you then visit. HabFit may
                collect IP address information in order to administer the Site
                and to gather broad demographic information.
              </Typography>
            </li>
            <li>
              <Typography>
                <i>Behavioral Advertising Information</i>. We may use tracking
                devices and cookies to collect non-personal information about
                the users of our Site. The information collected may help us
                provide recommendations to you, serve you with advertisements or
                direct you to a product or service that we believe may be of
                interest to you. The purpose of this data collection is to
                optimize your experience on our Site and to ensure that the
                advertisements offered to you are relevant. We take steps to
                ensure that the information we collect and share for advertising
                purposes remains anonymous. For more information about this type
                of advertising and to opt-out of receiving targeted
                advertisements from certain advertisers, please visit{' '}
                <Link
                  href="http://www.networkadvertising.org/managing/opt_out.asp"
                  target="_blank"
                >
                  here
                </Link>
                .
              </Typography>
            </li>
            <li>
              <Typography>
                <i>Geolocation Data</i>. We may also collect your location if
                you choose to allow our Platform to track your location or if
                your personal or non-personal information indicates a certain
                geographic location. This information may help us provide
                recommendations to you based on your location, serve you with
                advertisements or direct you to a product or service that we
                believe may be of interest to you and that is marketed to people
                near your location.
              </Typography>
            </li>
          </ul>
        </ul>
      </ul>
      <Box mt={3} />
      <Typography id="information-use" variant="h5" component="h2">
        Information Use
      </Typography>
      <Box mt={2} />
      <Typography>
        <b>Use of Personal Information Collected Via the Platform</b>. We use
        your personal information that is collected on the Platform primarily
        for the following purposes:
      </Typography>
      <ul className={classes.list}>
        <li>
          <Typography>
            To deliver the services that you requested via the Platform, such as
            workout sessions or other appointments
          </Typography>
        </li>
        <li>
          <Typography>
            To deliver you information, alerts, or newsletters that you request,
            or that we believe may be of interest to you
          </Typography>
        </li>
        <li>
          <Typography>
            To complete a transaction or service requested by you
          </Typography>
        </li>
        <li>
          <Typography>To fulfill the terms of a promotion</Typography>
        </li>
        <li>
          <Typography>
            To ensure the Platform is relevant to your needs
          </Typography>
        </li>
        <li>
          <Typography>
            To notify you about a material change to this Privacy Policy or the
            Terms of Use Agreement, if necessary
          </Typography>
        </li>
        <li>
          <Typography>
            To allow you access to limited-entry areas of the Platform
          </Typography>
        </li>
        <li>
          <Typography>
            <b>Use of Non-Personal Information</b>. We use non-personal
            information in the ways described above, either alone or in
            combination with personal information. We also may use non-personal
            information in other ways as permitted by applicable law.
          </Typography>
        </li>
      </ul>
      <Box mt={3} />
      <Typography id="third-party-links" variant="h5" component="h2">
        Third-Party Links
      </Typography>
      <Box mt={2} />
      <Typography>
        This Platform may contain links to other sites. Additionally, third
        parties may from time to time link to the Platform. PLEASE BE AWARE THAT
        WE ARE NOT RESPONSIBLE FOR THE CONTENT OR PRACTICES OF ANY OTHER
        WEBSITES. We encourage our users to read the privacy statements of each
        and every website that they visit.
      </Typography>
      <Box mt={3} />
      <Typography id="choice" variant="h5" component="h2">
        Choice
      </Typography>
      <Box mt={2} />
      <Typography>
        We communicate with users who subscribe to our services on a regular
        basis via email and messenger channels. For example, we may use your
        email address or messenger chat page to confirm a subscription request,
        to send you notice of changes to your scheduling request, to send you
        information about changes to our services, and to send notices and other
        disclosures as required by applicable law. Generally, users cannot
        opt-out of these communications, but they will be primarily
        informational in nature rather than promotional.
      </Typography>
      <Box mt={1} />
      <Typography>
        However, we may provide you the opportunity to exercise an opt-out
        choice if you do not want to receive other types of communication from
        us, such as messages or updates from us regarding new services and
        products offered on the Platform. The opt-out choice will be provided in
        the messages that we send to you when required to comply with applicable
        law. If you choose to opt-out of any message correspondence, we will
        process your unsubscribe as soon as possible, but please be aware that
        in some circumstances you may receive a few more messages until the
        unsubscribe is processed.
      </Typography>
      <Box mt={3} />
      <Typography id="notifications-changes" variant="h5" component="h2">
        Notification of Changes
      </Typography>
      <Box mt={2} />
      <Typography>
        If we decide to change our Privacy Policy, we will post those changes on
        our Privacy Policy so you will always be aware of what information we
        collect, how we use it, and under what circumstances we disclose it. If
        at any point we decide to use personal information in a manner
        materially different from that stated at the time it was collected, we
        will notify you by way of email or messenger. You will then have a
        choice as to whether or not we may use your information in this manner.
        We will use information in accordance with the Privacy Policy under
        which the information was collected.
        <br />
        Special Policy Regarding Information from Children Under Age Thirteen
      </Typography>
      <Box mt={1} />
      <Typography>
        <b>HabFit</b> is not targeted at children under the age of 13, and we do
        not knowingly collect personal information from any child under the age
        of 13 without parental consent. When we do receive such information, we
        delete it as soon as we discover it and we do not use it or share it
        with third parties.
      </Typography>
      <Box mt={3} />
      <Typography id="security" variant="h5" component="h2">
        Security
      </Typography>
      <Box mt={2} />
      <Typography>
        We make an effort to have reasonable security procedures in place to
        protect the loss, misuse, or alteration of the information under our
        control, including using Secure Sockets Layer (SSL) technology to
        collect and transmit your information. If you have any questions about
        the security of the Site, please contact{' '}
        <Link href="mailto:team@habfit.io?subject=Security%20question%20from%20Privacy%20Policy">
          team@habfit.io
        </Link>
        .
      </Typography>
      <Box mt={3} />
      <Typography id="insolvency" variant="h5" component="h2">
        Insolvency
      </Typography>
      <Box mt={2} />
      <Typography>
        In the unlikely event that HabFit ever becomes insolvent and seeks
        protection under applicable bankruptcy law, HabFit may share your
        personal information and non-personal information with a third party
        that intends to continue business subject to this Privacy Policy, as it
        may be amended from time to time.
      </Typography>
      <Box mt={3} />
      <Typography id="waiver-severability" variant="h5" component="h2">
        Waiver / Severability
      </Typography>
      <Box mt={2} />
      <Typography>
        The waiver by either party of a breach or right under this Privacy
        Policy will not constitute a waiver of any subsequent breach or right.
        If any provision of this Privacy Policy is found to be invalid or
        unenforceable by a court of competent jurisdiction, such provision shall
        be severed from the remainder of this Privacy Policy, which will
        otherwise remain in full force and effect.
      </Typography>
      <Box mt={3} />
      <Typography id="ccpa" variant="h5" component="h2">
        CCPA Privacy Rights (Do Not Sell My Personal Information)
      </Typography>
      <Box mt={2} />
      <Typography>
        Under the CCPA, among other rights, California consumers have the right
        to:
        <br />
        <br />
        Request that a business that collects a consumer&apos;s personal data
        disclose the categories and specific pieces of personal data that a
        business has collected about consumers.
        <br />
        Request that a business delete any personal data about the consumer that
        a business has collected.
        <br />
        Request that a business that sells a consumer&apos;s personal data, not
        sell the consumer&apos;s personal data.
        <br />
        If you make a request, we have one month to respond to you. If you would
        like to exercise any of these rights, please contact us.
      </Typography>
      <Box mt={3} />
      <Typography id="gdpr" variant="h5" component="h2">
        GDPR Data Protection Rights
      </Typography>
      <Box mt={2} />
      <Typography>
        We would like to make sure you are fully aware of all of your data
        protection rights. Every user is entitled to the following:
        <br />
        <br />
        The right to access – You have the right to request copies of your
        personal data. We may charge you a small fee for this service.
        <br />
        The right to rectification – You have the right to request that we
        correct any information you believe is inaccurate. You also have the
        right to request that we complete the information you believe is
        incomplete.
        <br />
        The right to erasure – You have the right to request that we erase your
        personal data, under certain conditions.
        <br />
        The right to restrict processing – You have the right to request that we
        restrict the processing of your personal data, under certain conditions.
        <br />
        The right to object to processing – You have the right to object to our
        processing of your personal data, under certain conditions.
        <br />
        The right to data portability – You have the right to request that we
        transfer the data that we have collected to another organization, or
        directly to you, under certain conditions.
        <br />
        If you make a request, we have one month to respond to you. If you would
        like to exercise any of these rights, please contact us.
      </Typography>
      <Box mt={3} />
      <Typography id="chldren-information" variant="h5" component="h2">
        Children&apos;s Information
      </Typography>
      <Box mt={2} />
      <Typography>
        Another part of our priority is adding protection for children while
        using the internet. We encourage parents and guardians to observe,
        participate in, and/or monitor and guide their online activity.
      </Typography>
      <Typography>
        HabFit does not knowingly collect any Personal Identifiable Information
        from children under the age of 13. If you think that your child provided
        this kind of information on our website, we strongly encourage you to
        contact us immediately and we will do our best efforts to promptly
        remove such information from our records.
      </Typography>
      <Box mt={3} />
      <Typography id="chldren-information" variant="h5" component="h2">
        Updates and Questions
      </Typography>
      <Box mt={2} />
      <Typography>
        We may update this Privacy Policy periodically without notice to you,
        other than posting the revised policy on our website at{' '}
        <Link to="./">habfit.io/privacy-policy</Link>. Once any changes are
        made, we will update the “Effective Date” at the top of this Policy. We
        encourage all users to check back on this policy frequently to stay
        updated on any potential changes.
      </Typography>
      <Box mt={1} />
      <Typography>
        Any questions about our Privacy Policy or the collection and use of
        personal data should be sent to us by email or regular mail at the
        addresses below.
      </Typography>
      <Box mt={2} />
      <Typography variant="subtitle1">
        Email{' '}
        <Link href="mailto:team@habfit.io?subject=Privacy%20Policy">
          team@habfit.io
        </Link>
      </Typography>
      <Box mt={1} />
      <Typography variant="subtitle1">Regular Mail</Typography>
      <Typography>
        HabFit Technologies Inc.
        <br />
        5307 Victoria Drive #498
        <br />
        Vancouver, BC V5P 3V6
      </Typography>
    </Container>
  );
};
